import { type ComponentProps, memo, useEffect, useRef } from 'react';
import { useCountUp } from 'react-countup';
import { useResizeDetector } from 'react-resize-detector';

import { cn } from '@air/utils-tailwind';

import { usePlayerStore } from './PlayerContextProvider';

export type PlayerTimeFormatFramesProps = ComponentProps<'div'> & {
  frameCount?: number;
};

export const PlayerTimeFormatFrames = memo(
  ({ className, frameCount = 0, ...restOfProps }: PlayerTimeFormatFramesProps) => {
    const countRef = useRef<HTMLSpanElement>(null);
    const currentTime = usePlayerStore((state) => state.currentTime);
    const duration = usePlayerStore((state) => state.duration);
    const { ref: resizeRef, width } = useResizeDetector({
      handleHeight: false,
    });
    const { update } = useCountUp({
      duration: 0.5,
      end: frameCount,
      ref: countRef,
    });

    useEffect(() => {
      const currentFrame = duration > 0 ? Math.floor((currentTime / duration) * frameCount) : 0;

      update(currentFrame);
    }, [currentTime, duration, frameCount, update]);

    return (
      <div
        className={cn('flex items-center gap-1 tabular-nums', className)}
        data-testid="PLAYER_TIME_FORMAT_FRAMES"
        {...restOfProps}
      >
        <span
          className="text-right ads:text-primary text-gray-12"
          data-testid="PLAYER_TIME_FORMAT_FRAMES_CURRENT"
          ref={countRef}
          style={{
            // We set the min-width here to the width of the left element to prevent the left element from shifting.
            minWidth: width,
          }}
        />
        <span className="ads:text-metadata text-gray-9" data-testid="PLAYER_TIME_FORMAT_FRAMES_SEPARATOR">
          /
        </span>
        <span className="ads:text-metadata text-gray-9" data-testid="PLAYER_TIME_FORMAT_FRAMES_TOTAL" ref={resizeRef}>
          {frameCount?.toLocaleString() ?? '-'}
        </span>
      </div>
    );
  },
);

PlayerTimeFormatFrames.displayName = 'PlayerTimeFormatFrames';
